// The Scout tab: the chat list and the open conversation (GRO-274).
//
// The list is the server's (`/api/chat/conversations`), so it follows the person across devices.
// Returning to the tab reopens the chat that was open, or the one still running; a chat that
// finished while the person was elsewhere carries a dot. Chats from before server history
// (this browser's localStorage) are still listed, below, and continue on the same Scout thread.

import { useEffect, useState } from 'react';
import { ChatPanel } from '../sidebar/ChatEmbed';
import { useChatStore, useChatVersion } from '../chat/ChatProvider';
import { readConversations } from '../lib/chatConversations.ts';
import { refreshConversations, threadKeyFor, threadView } from '../lib/chatStore.ts';
import type { ChatStore, ConversationSummary } from '../lib/chatStore.ts';

/** Where the tab opens: the chat open last time, else one still running, else a new one. */
export function initialConversationId(lastConversationId: string | null, conversations: ConversationSummary[]): string {
  if (lastConversationId) return lastConversationId;
  const running = conversations.find(conversation => conversation.status === 'running');
  if (running) return running.conversation_id;
  return crypto.randomUUID();
}

function listDate(updatedAt: string | number): string {
  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime()) || date.getTime() === 0) return 'Earlier chats';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function isRunning(store: ChatStore, actor: string, conversationId: string, serverStatus: string): boolean {
  const view = threadView(store, threadKeyFor(actor, { conversation_id: conversationId }));
  return view.running || (serverStatus === 'running' && !view.historyLoaded);
}

export function ScoutConversations({ actor }: { actor: string }) {
  const store = useChatStore();
  useChatVersion(store);
  const [activeId, setActiveId] = useState(() => initialConversationId(store.lastConversationId, store.conversations));
  const [historyOpen, setHistoryOpen] = useState(false);
  const [localConversations] = useState(() => readConversations(actor));

  // The list, fresh on arrival. The store keeps it between visits, so it renders at once.
  useEffect(() => { void refreshConversations(store, actor); }, [store, actor]);

  const serverIds = new Set(store.conversations.map(conversation => conversation.conversation_id));
  const browserOnly = localConversations.filter(conversation => conversation.id !== 'legacy' && !serverIds.has(conversation.id));
  const active = store.conversations.find(conversation => conversation.conversation_id === activeId);
  const activeLocal = localConversations.find(conversation => conversation.id === activeId);
  const activeKey = threadKeyFor(actor, { conversation_id: activeId });
  const activeLines = threadView(store, activeKey).lines;
  const title = active?.title || activeLocal?.title || activeLines.find(line => line.role === 'user')?.text.slice(0, 70) || 'New chat';

  function open(conversationId: string) {
    setActiveId(conversationId);
    setHistoryOpen(false);
  }

  return (
    <section className="scoutworkspace" aria-label="Scout chat workspace">
      <aside className={'scouthistory' + (historyOpen ? ' expanded' : '')} aria-label="Chat history">
        <div className="scouthistorytop">
          <button className="scouthistorytoggle" onClick={() => setHistoryOpen(!historyOpen)} aria-expanded={historyOpen} aria-controls="scout-chat-list">Chats</button>
          <span className="scouthistorylabel">Your chats</span>
          <button className="btn scoutnewchat" onClick={() => open(crypto.randomUUID())}>+ New chat</button>
        </div>
        <div id="scout-chat-list" className="scoutchatlist">
          {store.conversations.length === 0 && browserOnly.length === 0 ? <p className="scouthistoryempty">Your conversations will appear here.</p> : null}
          {store.conversations.map(conversation => {
            const key = threadKeyFor(actor, { conversation_id: conversation.conversation_id });
            const unread = store.unread.has(key) && activeId !== conversation.conversation_id;
            const running = isRunning(store, actor, conversation.conversation_id, conversation.status);
            return (
              <button key={conversation.conversation_id} className={'scoutchatitem' + (activeId === conversation.conversation_id ? ' selected' : '')}
                aria-current={activeId === conversation.conversation_id ? 'true' : undefined}
                onClick={() => open(conversation.conversation_id)} title={conversation.title}>
                <span>
                  {unread ? <span className="chatdot" aria-label="New answer" /> : null}
                  {conversation.title || 'New chat'}
                </span>
                <small>{running ? 'Working…' : listDate(conversation.updated_at)}</small>
              </button>
            );
          })}
          {browserOnly.length > 0 ? <p className="scouthistorynote">Earlier, on this browser</p> : null}
          {browserOnly.map(conversation => (
            <button key={conversation.id} className={'scoutchatitem' + (activeId === conversation.id ? ' selected' : '')}
              aria-current={activeId === conversation.id ? 'true' : undefined}
              onClick={() => open(conversation.id)} title={conversation.title}>
              <span>{conversation.title}</span>
              <small>{listDate(conversation.updatedAt)}</small>
            </button>
          ))}
        </div>
        <p className="scouthistorynote">History follows you across devices.</p>
      </aside>
      <main className="scoutconversation">
        <header className="scoutconversationheader">
          <span className="scoutlabel">Scout</span>
          <h1>{title}</h1>
        </header>
        <div className="scouthub">
          <ChatPanel key={activeId} workspace conversationId={activeId} initialLines={activeLocal?.lines} />
        </div>
      </main>
    </section>
  );
}
